import { Button } from "$components/button/button";
import { toast } from "$global-providers/toast-provider";
import { randomId } from "$routes/_lib/random-id";
import { linkToPriceList } from "$routes/price-list/links";
import { gql } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { styled } from "@material-ui/core";
import { FormProvider, useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";
import { useCreateCustomItemsMutation } from "./__generated__/create-custom-items-form.gql";
import AppendableCreateCustomItemsTable from "./appendable-create-custom-items-table";
import { type FormData, useFormSchema } from "./use-form-schema";

gql`
  mutation createCustomItems($input: CreateCustomItemsInput!) {
    priceListV2 {
      createCustomItems(input: $input) {
        items {
          id
          name
        }
      }
    }
  }
`;

const ButtonsContainer = styled("div")(({ theme }) => ({
  alignSelf: "flex-end",
  display: "flex",
  justifyContent: "flex-end",
  gap: theme.spacing(3),
}));

const CreateCustomItemsForm: FC<{ priceListId: string }> = ({
  priceListId,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo =
    (location.state as { from?: string } | null)?.from ??
    linkToPriceList(priceListId);
  const { formatMessage } = useIntl();
  const formSchema = useFormSchema();

  const [addCustomItems] = useCreateCustomItemsMutation({
    onCompleted: (data) => {
      toast.success(
        formatMessage(
          {
            id: "pricelist.createCustomItems.success",
            defaultMessage:
              "Successfully created {quantity, number} custom items",
          },
          {
            quantity: data.priceListV2.createCustomItems.items.length,
          },
        ),
      );
      navigate(returnTo, { replace: true });
    },
    onError: (error) => {
      toast.error(
        formatMessage(
          {
            id: "priceList.createCustomItems.error.msg",
            defaultMessage: "Failed to create custom items: {message}",
          },
          {
            message: error.message,
          },
        ),
      );
    },
  });

  const createCustomItemsFormMethods = useForm<FormData>({
    mode: "onTouched",

    resolver: yupResolver(formSchema),
    defaultValues: {
      items: [{ id: randomId() }],
    },
  });

  const handleSubmit: Parameters<
    (typeof createCustomItemsFormMethods)["handleSubmit"]
  >[0] = (data) =>
    addCustomItems({
      variables: {
        input: { names: data.items.map((item) => item.name), priceListId },
      },
    });

  return (
    <FormProvider {...createCustomItemsFormMethods}>
      <form onSubmit={createCustomItemsFormMethods.handleSubmit(handleSubmit)}>
        <AppendableCreateCustomItemsTable dense />
        <ButtonsContainer>
          <Button
            color="secondary"
            onClick={() => navigate(returnTo, { replace: true })}
            data-testid="priceList.createCustomItemsPage.back.button"
          >
            <FormattedMessage
              id="priceList.createCustomItemsPage.back.button"
              defaultMessage="Back"
            />
          </Button>
          <Button
            type="submit"
            color="primary"
            data-testid="priceList.createCustomItemsPage.save.button"
          >
            <FormattedMessage
              id="priceList.createCustomItemsPage.save.button"
              defaultMessage="Save"
            />
          </Button>
        </ButtonsContainer>
      </form>
    </FormProvider>
  );
};

export default CreateCustomItemsForm;
